import {
  formatCurrency,
  formatString,
  timezones,
  formatDateTime,
  getTimezoneDate,
  capitalizeString,
  formatSeatRange,
  formatPhoneNumber,
} from '../../../utilities/helpers'
import {
  aggregatePriceObjectDisplayTotals,
  getPriceObjectEstimatedProcessingFee,
  isHensonFundReportEnabled,
} from '@blocktickets/shared-utils'

export const DEFAULT_TEMPLATE_VALUE = 'default'
export const AUX_ADMIN_TEMPLATE_VALUE = 'aux-admin'

export const PAYOUT_COLUMN_DEFINITIONS = [
  { id: 'name_of_event', label: 'Name of event' },
  { id: 'event_date', label: 'Event date' },
  { id: 'event_id', label: 'Event id' },
  { id: 'order_id', label: 'Order id' },
  { id: 'order_date', label: 'Order date' },
  { id: 'payment_plan', label: 'Payment plan' },
  { id: 'installments', label: 'Installments' },
  { id: 'amount_remaining', label: 'Amount remaining' },
  { id: 'first_name', label: 'First name' },
  { id: 'last_name', label: 'Last name' },
  { id: 'email', label: 'Email' },
  { id: 'phone_number', label: 'Phone number' },
  { id: 'sales_channel', label: 'Sales channel' },
  { id: 'offer_name', label: 'Offer name' },
  { id: 'section', label: 'Section' },
  { id: 'row', label: 'Row' },
  { id: 'seats', label: 'Seats' },
  { id: 'quantity', label: 'Quantity' },
  { id: 'base_price', label: 'Base price' },
  { id: 'service_fee', label: 'Service fee' },
  { id: 'facility_fee', label: 'Facility fee' },
  { id: 'henson_fee', label: 'Henson fee', hensonOnly: true },
  { id: 'sales_tax', label: 'Sales tax' },
  { id: 'chargeBack_protection', label: 'Chargeback protection' },
  { id: 'processing_fee', label: 'Processing fee' },
  { id: 'gross_sales', label: 'Gross sales' },
  { id: 'net_sales', label: 'Net sales' },
  { id: 'payment_method', label: 'Payment method' },
  { id: 'account_type', label: 'Account type' },
]

const DEFAULT_VISIBLE_IDS = new Set(
  PAYOUT_COLUMN_DEFINITIONS.filter((col) => !col.hensonOnly).map(
    (col) => col.id
  )
)

const AUX_ADMIN_VISIBLE_IDS = [
  'name_of_event',
  'event_date',
  'event_id',
  'order_id',
  'order_date',
  'net_sales',
]

export const isRefundedTransaction = (transaction) =>
  String(transaction?.status || '').toLowerCase() === 'refunded'

export const parseDecimal = (value) => {
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : 0
}

const parseOptionalDecimal = (value) => {
  if (value === null || value === undefined || value === '') return undefined
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : undefined
}

export const hasPayoutScopedNet = (transaction) =>
  parseOptionalDecimal(transaction?.payoutNet) !== undefined

/**
 * A payout-scoped net is the signed amount that actually settled in this
 * deposit, including refunds. Dropping those rows makes the event distribution
 * disagree with the payout and with the order lines.
 * Refunded orders that are not scoped to this payout stay out, so a later
 * refund does not subtract the whole historical order from an earlier deposit.
 */
export const shouldIncludeInPayoutTotals = (transaction) => {
  if (hasPayoutScopedNet(transaction)) return true
  return !isRefundedTransaction(transaction)
}

export const getSignedAmount = (transaction, value) => {
  const amount = Number(value || 0)
  if (!isRefundedTransaction(transaction)) return amount
  const absoluteAmount = Math.abs(amount)
  return absoluteAmount === 0 ? 0 : -absoluteAmount
}

export const getInstallments = (transaction) =>
  transaction?.payment_plan?.installments || []

export const getAmountRemaining = (transaction) =>
  parseDecimal(
    transaction?.payment_plan?.amountRemaining ??
      transaction?.payment_plan?.amount_remaining
  )

export const getTransactionNet = (transaction) =>
  parseOptionalDecimal(transaction?.payoutNet) ??
  parseOptionalDecimal(transaction?.destinationAccountNet) ??
  parseOptionalDecimal(transaction?.net)

export const getDisplayNetSales = (transaction) => {
  const netValue = getTransactionNet(transaction)
  // payoutNet is already the signed Stripe BT total for this payout.
  if (hasPayoutScopedNet(transaction)) return parseDecimal(netValue)
  return getSignedAmount(transaction, netValue)
}

export const sumPayoutNetSales = (transactions = []) =>
  transactions.reduce((sum, transaction) => {
    if (!shouldIncludeInPayoutTotals(transaction)) return sum
    return sum + getDisplayNetSales(transaction)
  }, 0)

export const getAvailableColumns = (organizationUUID) => {
  const includeHenson = isHensonFundReportEnabled(organizationUUID)
  return PAYOUT_COLUMN_DEFINITIONS.filter(
    (col) => !col.hensonOnly || includeHenson
  ).map((col) => ({
    id: col.id,
    label: col.label,
    hensonOnly: Boolean(col.hensonOnly),
  }))
}

export const getDefaultColumns = (organizationUUID) =>
  getAvailableColumns(organizationUUID).map((col) => ({
    ...col,
    visible: DEFAULT_VISIBLE_IDS.has(col.id),
  }))

export const getAuxAdminColumns = (organizationUUID) => {
  const available = getAvailableColumns(organizationUUID)
  const byId = new Map(available.map((col) => [col.id, col]))
  const ordered = []
  const seen = new Set()

  for (const id of AUX_ADMIN_VISIBLE_IDS) {
    const base = byId.get(id)
    if (!base) continue
    seen.add(id)
    ordered.push({ ...base, visible: true })
  }

  for (const col of available) {
    if (seen.has(col.id)) continue
    ordered.push({ ...col, visible: false })
  }

  return ordered
}

export const serializeColumnsForTemplate = (columns = []) =>
  columns.map((col) => ({
    id: col.id,
    visible: Boolean(col.visible),
  }))

export const mergeSavedColumnsWithDefaults = (
  savedColumns = [],
  organizationUUID
) => {
  const defaultColumns = getDefaultColumns(organizationUUID)
  const defaultById = new Map(defaultColumns.map((col) => [col.id, col]))
  const seen = new Set()
  const merged = []

  for (const saved of savedColumns) {
    const base = defaultById.get(saved.id)
    if (!base || seen.has(saved.id)) continue
    seen.add(saved.id)
    merged.push({ ...base, visible: Boolean(saved.visible) })
  }

  for (const defaultCol of defaultColumns) {
    if (!seen.has(defaultCol.id)) {
      merged.push(defaultCol)
    }
  }

  return merged
}

export const getPayoutEventDisplayName = (transaction) => {
  if (transaction?.access_pass_template?.name) {
    return transaction.access_pass_template.name
  }
  if (transaction?.packageOrder && transaction?.package?.name) {
    return transaction.package.name
  }
  return (
    transaction?.event?.name ||
    transaction?.flex_pack?.name ||
    transaction?.package?.name ||
    'N/A'
  )
}

export const formatPayoutTransactionRow = (transaction) => {
  const {
    orderId,
    event,
    flex_pack,
    package: eventPackage,
    users_permissions_user,
    processedAt,
    originalTickets,
    priceObject,
    intentDetails,
    total,
    source,
    estimatedProcessingFee,
    processingFee,
    facilityFee,
    hensonFee,
  } = transaction

  const timezone = timezones(
    event?.venue?.timezone ||
      flex_pack?.venue?.timezone ||
      eventPackage?.venue?.timezone
  )
  const priceObjectTotals = aggregatePriceObjectDisplayTotals(priceObject)
  const facilityFeeValue =
    parseDecimal(facilityFee) || parseDecimal(priceObjectTotals.facilityFee)

  return {
    name_of_event: getPayoutEventDisplayName(transaction),
    event_date: event
      ? formatDateTime(getTimezoneDate(event?.start, timezone))
      : 'N/A',
    event_id: event?.id ?? 'N/A',
    order_id: orderId,
    order_date: processedAt
      ? formatDateTime(getTimezoneDate(processedAt, timezone))
      : 'N/A',
    payment_plan: transaction?.payment_plan ? 'True' : 'False',
    installments: getInstallments(transaction).length,
    amount_remaining: getAmountRemaining(transaction),
    first_name: users_permissions_user
      ? capitalizeString(users_permissions_user?.firstName)
      : 'N/A',
    last_name: users_permissions_user
      ? capitalizeString(users_permissions_user?.lastName)
      : 'N/A',
    email: users_permissions_user?.email ?? 'N/A',
    phone_number: formatPhoneNumber(users_permissions_user?.phoneNumber),
    sales_channel: formatString(source),
    offer_name:
      originalTickets?.length > 0 ? originalTickets[0]?.offer?.name : 'N/A',
    section:
      originalTickets?.length > 0 ? originalTickets[0]?.sectionNumber : 'N/A',
    row: originalTickets?.length > 0 ? originalTickets[0]?.rowNumber : 'N/A',
    seats:
      originalTickets?.length > 0
        ? formatSeatRange(originalTickets?.map((t) => t.seatNumber))
        : 'N/A',
    quantity:
      originalTickets?.length > 0
        ? originalTickets?.length
        : flex_pack?.gameTickets,
    base_price:
      priceObjectTotals.offerPrice || priceObjectTotals.basePrice || 0,
    service_fee: priceObjectTotals.serviceFee || 0,
    facility_fee: getSignedAmount(transaction, facilityFeeValue),
    henson_fee: getSignedAmount(transaction, parseDecimal(hensonFee)),
    sales_tax: priceObjectTotals.salesTax || 0,
    chargeBack_protection: priceObjectTotals.chargeBackProtection || 0,
    processing_fee:
      estimatedProcessingFee ??
      processingFee ??
      getPriceObjectEstimatedProcessingFee(
        priceObject,
        priceObjectTotals.paymentProcessingFee ||
          priceObjectTotals.estimatedPaymentProcessingFee
      ) ??
      0,
    gross_sales: getSignedAmount(transaction, total),
    net_sales: getDisplayNetSales(transaction),
    payment_method: intentDetails?.charges?.data[0]?.payment_method_details
      ?.card?.brand
      ? capitalizeString(
          intentDetails?.charges?.data[0]?.payment_method_details?.card?.brand
        )
      : 'N/A',
    account_type:
      transaction?.destinationAccountNet !== null &&
      transaction?.destinationAccountNet !== undefined
        ? 'Destination'
        : 'Platform',
  }
}

export const pickVisibleRowValues = (row, visibleColumns = []) => {
  const picked = {}
  for (const col of visibleColumns) {
    picked[col.id] = row[col.id]
  }
  return picked
}

export const getEventGroupKey = (transaction) => {
  if (transaction?.access_pass_template?.name) {
    if (transaction.access_pass_template.id != null) {
      return `access-pass:${transaction.access_pass_template.id}`
    }
    return `access-pass:${String(transaction.access_pass_template.name)
      .trim()
      .toLowerCase()}`
  }
  if (transaction?.packageOrder && transaction?.package?.id != null) {
    return `package:${transaction.package.id}`
  }
  if (transaction?.packageOrder && transaction?.package?.name) {
    return `package:${String(transaction.package.name).trim().toLowerCase()}`
  }
  if (transaction?.event?.id != null) {
    return `event:${transaction.event.id}`
  }
  if (transaction?.flex_pack?.name) {
    return `flex:${String(transaction.flex_pack.name).trim().toLowerCase()}`
  }
  if (transaction?.event?.name) {
    return `name:${String(transaction.event.name).trim().toLowerCase()}`
  }
  if (transaction?.package?.name) {
    return `package:${String(transaction.package.name).trim().toLowerCase()}`
  }
  return 'unmatched'
}

export const getEventGroupLabel = (transaction) => {
  const name = getPayoutEventDisplayName(transaction)
  return name === 'N/A' ? 'Unmatched' : name
}

export const buildEventSummaries = (
  transactions = [],
  { includeHensonFee = false } = {}
) => {
  const groups = new Map()

  for (const transaction of transactions) {
    if (!shouldIncludeInPayoutTotals(transaction)) continue

    const key = getEventGroupKey(transaction)
    const label = getEventGroupLabel(transaction)
    const row = formatPayoutTransactionRow(transaction)
    const existing = groups.get(key) || {
      key,
      label,
      facilityFee: 0,
      hensonFee: 0,
      netPayout: 0,
    }

    existing.facilityFee += parseDecimal(row.facility_fee)
    existing.hensonFee += parseDecimal(row.henson_fee)
    existing.netPayout += parseDecimal(row.net_sales)
    groups.set(key, existing)
  }

  const summaries = [...groups.values()].map((group) => {
    const hensonFee = includeHensonFee ? group.hensonFee : 0
    const sales = group.netPayout - group.facilityFee - hensonFee
    return {
      key: group.key,
      label: `${group.label} Totals`,
      facilityFee: Math.round(group.facilityFee * 100) / 100,
      hensonFee: Math.round(hensonFee * 100) / 100,
      sales: Math.round(sales * 100) / 100,
      netPayout: Math.round(group.netPayout * 100) / 100,
    }
  })

  const totals = summaries.reduce(
    (acc, row) => {
      acc.facilityFee += row.facilityFee
      acc.hensonFee += row.hensonFee
      acc.sales += row.sales
      acc.netPayout += row.netPayout
      return acc
    },
    { facilityFee: 0, hensonFee: 0, sales: 0, netPayout: 0 }
  )

  return {
    rows: summaries,
    total: {
      label: 'Total',
      facilityFee: Math.round(totals.facilityFee * 100) / 100,
      hensonFee: Math.round(totals.hensonFee * 100) / 100,
      sales: Math.round(totals.sales * 100) / 100,
      netPayout: Math.round(totals.netPayout * 100) / 100,
    },
  }
}

export const getBuiltInTemplateOptions = (organizationUUID) => [
  { value: DEFAULT_TEMPLATE_VALUE, label: 'Default view' },
  { value: AUX_ADMIN_TEMPLATE_VALUE, label: 'Aux Admin' },
]

export const applyBuiltInTemplate = (templateId, organizationUUID) => {
  if (templateId === AUX_ADMIN_TEMPLATE_VALUE) {
    return {
      id: AUX_ADMIN_TEMPLATE_VALUE,
      name: 'Aux Admin',
      columns: getAuxAdminColumns(organizationUUID),
      includeEventSummary: true,
      builtIn: true,
    }
  }

  return {
    id: DEFAULT_TEMPLATE_VALUE,
    name: 'Default view',
    columns: getDefaultColumns(organizationUUID),
    includeEventSummary: false,
    builtIn: true,
  }
}

export const formatCurrencyCell = (value) => formatCurrency(Number(value || 0))
